import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Coffee, CheckCircle, Spinner } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'nova_employee_meal_draft';

const MEAL_OPTIONS = ['Breakfast', 'Lunch', 'Breakfast + Lunch'];
const FREQ_OPTIONS = ['Daily', 'Weekly', 'Monthly'];

const emptyForm = {
  company_name: '',
  contact_person: '',
  mobile: '',
  email: '',
  employee_count: '',
  meal_requirement: '',
  frequency: '',
  start_date: '',
  office_location: '',
  special_requirements: '',
};

export const EmployeeMealEnquiryForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Restore draft on mount, persist on change
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setForm({ ...emptyForm, ...JSON.parse(raw) });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!submitted) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
    }
  }, [form, submitted]);

  const update = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = 'Required';
    if (!form.contact_person.trim()) e.contact_person = 'Required';
    if (!/^[\d +()-]{7,20}$/.test(form.mobile)) e.mobile = 'Enter a valid mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    const empCount = parseInt(form.employee_count, 10);
    if (!empCount || empCount < 1) e.employee_count = 'Enter number of employees';
    if (!form.meal_requirement) e.meal_requirement = 'Please select';
    if (!form.frequency) e.frequency = 'Please select';
    if (!form.office_location.trim()) e.office_location = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return; // guard against duplicate submissions
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        employee_count: parseInt(form.employee_count, 10),
      };
      const resp = await axios.post(`${BACKEND_URL}/api/catering/employee-meal-enquiry`, payload);
      if (resp.data?.request_id) {
        setSubmitted(true);
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        toast.success('Enquiry submitted successfully.');
      } else {
        throw new Error('Unexpected response');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="employee-meal-enquiry"
      className="relative px-6 py-16 md:py-20 bg-gradient-to-br from-[#FEF6F1] via-[#FFF8F4] to-[#FEF6F1]"
      data-testid="employee-meal-section"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#C05C3D]/30 text-[#C05C3D] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-4 shadow-sm">
            <Coffee size={14} weight="fill" /> Daily Employee Meals
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1C1917] font-heading mb-3">
            Plan Your Employee Meals
          </h2>
          <p className="text-stone-600 max-w-xl mx-auto">
            Tell us what your team needs. Our catering team will review your requirements and share a suitable brochure, menu and pricing.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white border border-stone-200/80 rounded-3xl p-6 md:p-10 shadow-lg"
        >
          {submitted ? (
            <div className="text-center py-8" data-testid="employee-success">
              <div className="w-16 h-16 rounded-full bg-[#C05C3D]/10 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={36} weight="duotone" className="text-[#C05C3D]" />
              </div>
              <h3 className="text-2xl font-semibold text-[#1C1917] font-heading mb-3">Thank you!</h3>
              <p className="text-stone-600 max-w-lg mx-auto">
                Your request has been received. Our catering team will review your requirements and contact you with suitable menu, brochure and pricing options.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm(emptyForm); }}
                className="mt-6 text-[#C05C3D] font-semibold hover:underline"
                data-testid="employee-submit-another"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="employee-enquiry-form" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Company Name *" error={errors.company_name}>
                  <input type="text" value={form.company_name} onChange={update('company_name')} className={inputCls(errors.company_name)} data-testid="emp-company" />
                </Field>
                <Field label="Contact Person *" error={errors.contact_person}>
                  <input type="text" value={form.contact_person} onChange={update('contact_person')} className={inputCls(errors.contact_person)} data-testid="emp-contact" />
                </Field>
                <Field label="Mobile Number *" error={errors.mobile}>
                  <input type="tel" value={form.mobile} onChange={update('mobile')} className={inputCls(errors.mobile)} data-testid="emp-mobile" placeholder="+91 98xxxxxxxx" />
                </Field>
                <Field label="Email *" error={errors.email}>
                  <input type="email" value={form.email} onChange={update('email')} className={inputCls(errors.email)} data-testid="emp-email" />
                </Field>
                <Field label="Number of Employees *" error={errors.employee_count}>
                  <input type="number" min="1" value={form.employee_count} onChange={update('employee_count')} className={inputCls(errors.employee_count)} data-testid="emp-count" />
                </Field>
                <Field label="Meal Requirement *" error={errors.meal_requirement}>
                  <select value={form.meal_requirement} onChange={update('meal_requirement')} className={inputCls(errors.meal_requirement)} data-testid="emp-meal">
                    <option value="">Select…</option>
                    {MEAL_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Frequency *" error={errors.frequency}>
                  <select value={form.frequency} onChange={update('frequency')} className={inputCls(errors.frequency)} data-testid="emp-frequency">
                    <option value="">Select…</option>
                    {FREQ_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Field>
                <Field label="Preferred Start Date" error={errors.start_date}>
                  <input type="date" value={form.start_date} onChange={update('start_date')} className={inputCls(errors.start_date)} data-testid="emp-start-date" />
                </Field>
                <Field label="Office Location *" error={errors.office_location} colSpan>
                  <input type="text" value={form.office_location} onChange={update('office_location')} className={inputCls(errors.office_location)} data-testid="emp-location" placeholder="City / Area" />
                </Field>
              </div>
              <Field label="Special Requirements" error={errors.special_requirements}>
                <textarea rows="4" value={form.special_requirements} onChange={update('special_requirements')} className={inputCls(errors.special_requirements)} data-testid="emp-special" placeholder="Dietary preferences, allergens, cuisine type, timing constraints, etc." />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C05C3D] hover:bg-[#B84E32] text-white font-semibold text-base px-6 py-4 rounded-full transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="emp-submit"
              >
                {submitting ? (
                  <>
                    <Spinner size={20} weight="bold" className="animate-spin" /> Submitting…
                  </>
                ) : (
                  <>Request Meal Plan →</>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const inputCls = (err) => `w-full px-4 py-3 border rounded-xl bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#C05C3D]/40 transition-all ${err ? 'border-red-500' : 'border-stone-300'}`;

const Field = ({ label, error, children, colSpan }) => (
  <div className={colSpan ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-semibold text-stone-700 mb-1.5">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600" data-testid="field-error">{error}</p>}
  </div>
);

export default EmployeeMealEnquiryForm;
